import { useMemo, useState } from 'react';

import { StarFilled } from '@ant-design/icons';
import { Button, Checkbox, Input, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { Store } from 'models/Store';

import {
  SupervisionMap,
  commonSupervisor,
  firstName,
  hasSupervisor,
  storeKey,
  storeLocation,
} from '../data/supervision';

interface SupervisorDistributionProps {
  stores: Store[];
  sups: string[];
  eff: SupervisionMap;
  missingCount: number;
  onSet: (storeIds: string[], sup: string) => void;
  onAuto: () => void;
}

interface SupervisorPickerProps {
  sups: string[];
  value: string | null | undefined;
  onPick: (sup: string) => void;
  small?: boolean;
}

function SupervisorPicker({
  sups,
  value,
  onPick,
  small,
}: SupervisorPickerProps) {
  return (
    <div
      className={`supervisor-step__picker${
        small ? ' supervisor-step__picker--small' : ''
      }`}
      role="radiogroup"
    >
      {sups.map((sup) => {
        const on = value === sup;
        return (
          <button
            key={sup}
            type="button"
            role="radio"
            aria-checked={on}
            title={sup}
            className={`supervisor-step__picker-option${
              on ? ' supervisor-step__picker-option--on' : ''
            }`}
            onClick={() => onPick(sup)}
          >
            {firstName(sup)}
          </button>
        );
      })}
    </div>
  );
}

function SupervisorDistribution({
  stores,
  sups,
  eff,
  missingCount,
  onSet,
  onAuto,
}: SupervisorDistributionProps) {
  const { t } = useTranslation();
  const i18nBase =
    'scenes.requirements_operation.components.assign_people.supervisor_step';

  const [locs, setLocs] = useState<string[]>([]);
  const [term, setTerm] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(false);

  const capitalLocs = useMemo(
    () =>
      [
        ...new Set(
          stores
            .filter((s) => s.regionKind === 'CAPITAL')
            .map((s) => storeLocation(s))
        ),
      ].sort(),
    [stores]
  );
  const regionLocs = useMemo(
    () =>
      [
        ...new Set(
          stores
            .filter((s) => s.regionKind !== 'CAPITAL')
            .map((s) => storeLocation(s))
        ),
      ].sort(),
    [stores]
  );

  const normalizedTerm = term.trim().toLowerCase();
  const visible = stores.filter(
    (s) =>
      (!locs.length || locs.includes(storeLocation(s))) &&
      (!onlyMissing || !hasSupervisor(s, sups, eff)) &&
      (!normalizedTerm ||
        [s.name, s.chain, storeLocation(s)].some((v) =>
          (v || '').toLowerCase().includes(normalizedTerm)
        ))
  );
  const groups = [...new Set(visible.map((s) => s.chain))]
    .sort()
    .map((chain) => ({
      chain,
      items: visible.filter((s) => s.chain === chain),
    }));

  const hasFilters = locs.length > 0 || !!term || onlyMissing;
  const toggleLoc = (loc: string) =>
    setLocs((prev) =>
      prev.includes(loc) ? prev.filter((x) => x !== loc) : [...prev, loc]
    );
  const clearFilters = () => {
    setLocs([]);
    setTerm('');
    setOnlyMissing(false);
  };

  return (
    <div className="supervisor-step__distribution">
      <div className="supervisor-step__distribution-head">
        <span className="supervisor-step__label">
          {t(`${i18nBase}.distribute_title`)}
        </span>
        <Button size="small" shape="round" onClick={onAuto}>
          {t(`${i18nBase}.buttons.auto_split`)}
        </Button>
        {missingCount > 0 && (
          <span className="supervisor-step__missing">
            {t(`${i18nBase}.missing`, { count: missingCount })}
          </span>
        )}
      </div>

      <div className="supervisor-step__filters">
        <div className="supervisor-step__filters-row">
          <Input
            allowClear
            className="supervisor-step__search"
            value={term}
            placeholder={t(`${i18nBase}.filters.search_placeholder`)}
            onChange={(e) => setTerm(e.target.value)}
          />
          <Checkbox
            checked={onlyMissing}
            onChange={(e) => setOnlyMissing(e.target.checked)}
          >
            {t(`${i18nBase}.filters.only_missing`)}
          </Checkbox>
          {hasFilters && (
            <Button
              size="small"
              shape="round"
              className="supervisor-step__clear"
              onClick={clearFilters}
            >
              {t(`${i18nBase}.filters.clear`)}
            </Button>
          )}
        </div>

        {capitalLocs.length > 0 && (
          <div className="supervisor-step__filters-row">
            <span className="supervisor-step__loc-label">
              {t(`${i18nBase}.filters.capital`)}
            </span>
            {capitalLocs.map((loc) => (
              <Tag.CheckableTag
                key={loc}
                className="supervisor-step__loc-chip"
                checked={locs.includes(loc)}
                onChange={() => toggleLoc(loc)}
              >
                {loc}
              </Tag.CheckableTag>
            ))}
          </div>
        )}
        {regionLocs.length > 0 && (
          <div className="supervisor-step__filters-row">
            <span className="supervisor-step__loc-label">
              {t(`${i18nBase}.filters.regions`)}
            </span>
            {regionLocs.map((loc) => (
              <Tag.CheckableTag
                key={loc}
                className="supervisor-step__loc-chip"
                checked={locs.includes(loc)}
                onChange={() => toggleLoc(loc)}
              >
                {loc}
              </Tag.CheckableTag>
            ))}
          </div>
        )}

        {visible.length > 0 && hasFilters && (
          <div className="supervisor-step__filters-row supervisor-step__bulk">
            <span className="supervisor-step__label">
              {t(`${i18nBase}.filters.bulk_assign`, { count: visible.length })}
            </span>
            <SupervisorPicker
              small
              sups={sups}
              value={commonSupervisor(visible, eff)}
              onPick={(sup) => onSet(visible.map(storeKey), sup)}
            />
          </div>
        )}
      </div>

      {groups.length === 0 ? (
        <div className="supervisor-step__empty">
          {t(`${i18nBase}.no_matches`)}
        </div>
      ) : (
        <div className="supervisor-step__groups">
          {groups.map((group) => (
            <div key={group.chain} className="supervisor-step__group">
              <div className="supervisor-step__group-head">
                <span className="supervisor-step__group-name">
                  {group.chain}
                </span>
                <span className="supervisor-step__group-count">
                  {t(`${i18nBase}.store_count`, {
                    count: group.items.length,
                  })}
                </span>
                <div className="supervisor-step__group-actions">
                  <span className="supervisor-step__group-hint">
                    {t(`${i18nBase}.whole_chain`)}
                  </span>
                  <SupervisorPicker
                    small
                    sups={sups}
                    value={commonSupervisor(group.items, eff)}
                    onPick={(sup) => onSet(group.items.map(storeKey), sup)}
                  />
                </div>
              </div>
              {group.items.map((store) => {
                const assigned = hasSupervisor(store, sups, eff);
                return (
                  <div
                    key={store.id}
                    className={`supervisor-step__store${
                      assigned ? '' : ' supervisor-step__store--missing'
                    }`}
                  >
                    <div className="supervisor-step__store-main">
                      <div className="supervisor-step__store-name">
                        {store.isVip && (
                          <StarFilled className="supervisor-step__star" />
                        )}
                        <span>{store.name}</span>
                      </div>
                      <div className="supervisor-step__store-meta">
                        {store.regionKind === 'CAPITAL'
                          ? t(`${i18nBase}.location_capital`, {
                              loc: storeLocation(store),
                            })
                          : t(`${i18nBase}.location_region`, {
                              loc: storeLocation(store),
                            })}
                      </div>
                    </div>
                    <SupervisorPicker
                      sups={sups}
                      value={assigned ? eff[storeKey(store)] : null}
                      onPick={(sup) => onSet([storeKey(store)], sup)}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SupervisorDistribution;
